import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { OFFICE_ATTENDANCE_REASONS, officeAttendanceBody } from './attendanceRules';

const STATUS = [['ATTENDED', 'Attended'], ['PARTIAL', 'Partially delivered'], ['NO_SHOW', 'Not delivered']];
const label = (s) => (s === 'ATTENDED' ? 'Attended' : s === 'PARTIAL' ? 'Partially delivered' : s === 'NO_SHOW' ? 'Not delivered' : s);

/** UA-8 F35: office "Record attendance" per participant (§31 record, carried to that participant's segments; feeds readiness). */
export function ParticipantAttendancePanel({ shift, segments, canRecord, onChanged }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const shiftId = shift?.id;
  const load = async () => { try { const r = await api.get(`/shifts/${shiftId}/attendance`); setRows(r.data?.data || []); } catch { setRows([]); } };
  useEffect(() => { if (shiftId) load(); }, [shiftId, shift?.updated_at]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Shared shifts: the shift only carries the anchor participant's name, so look up the other linked participants.
  const [names, setNames] = useState({});
  const anchorId = String(shift?.participant_id || '');
  const otherIds = [...new Set([...(shift?.linked_participants || []), ...(segments || []).filter((r) => !r.participant_name).map((r) => r.participant_id)]
    .filter(Boolean).map(String))].filter((pid) => pid !== anchorId);
  const nameOf = (pid, given) => given || (String(pid) === anchorId ? shift?.participant_name : names[String(pid)]) || 'Participant';
  useEffect(() => {
    const missing = otherIds.filter((pid) => !names[pid]);
    if (!missing.length) return;
    Promise.all(missing.map(async (pid) => {
      try { const r = await api.get(`/participants/${pid}`); const d = r.data?.data || r.data || {}; return [pid, `${d.first_name || ''} ${d.last_name || ''}`.trim()]; }
      catch { return [pid, '']; }
    })).then((pairs) => setNames((n) => ({ ...n, ...Object.fromEntries(pairs) })));
  }, [otherIds.join(',')]);   // eslint-disable-line react-hooks/exhaustive-deps
  const participants = [];
  const seen = new Set();
  (segments || []).filter((r) => r.status !== 'DELETED' && r.participant_id).forEach((r) => {
    if (seen.has(String(r.participant_id))) return;
    seen.add(String(r.participant_id));
    participants.push({ id: String(r.participant_id), name: nameOf(r.participant_id, r.participant_name), start: r.allocation_start_time, end: r.allocation_end_time });
  });
  [...(shift?.linked_participants || []), shift?.participant_id].filter(Boolean).forEach((pid) => {
    if (!seen.has(String(pid))) { seen.add(String(pid)); participants.push({ id: String(pid), name: nameOf(pid), start: shift.start_time, end: shift.end_time }); }
  });
  if (participants.length === 0) return null;
  const byPid = Object.fromEntries(rows.map((r) => [String(r.participant_id), r]));
  return (
    <Card data-testid="participant-attendance-panel">
      <CardHeader className="pb-2 px-3 pt-3"><CardTitle className="text-sm">Participant attendance / delivery outcome</CardTitle></CardHeader>
      <CardContent className="p-3 pt-0 space-y-2 text-xs">
        <p className="text-[11px] text-muted-foreground">Recorded per participant and carried to each of their service segments. Mandatory for billing readiness — never inferred from a worker clock.</p>
        {participants.map((p) => {
          const att = byPid[p.id];
          return (
            <div key={p.id} className="rounded border p-2 space-y-1" data-testid={`participant-attendance-row-${p.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-medium">{p.name}</span>
                <span className="text-muted-foreground tabular-nums">{p.start}–{p.end}</span>
                {att ? <Badge variant="outline" className="text-[10px] py-0" data-testid={`participant-attendance-status-${p.id}`}>{label(att.status)}{att.approved ? ' · approved' : ' · awaiting approval'}</Badge>
                  : <span className="text-amber-700" data-testid={`participant-attendance-missing-${p.id}`}>Attendance not recorded</span>}
                {canRecord && !att?.approved && shift.status !== 'CANCELLED' && (
                  <Button size="sm" variant="outline" className="ml-auto h-7" onClick={() => setEditing(editing === p.id ? null : p.id)} data-testid={`record-attendance-${p.id}`}>{att ? 'Update attendance' : 'Record attendance'}</Button>
                )}
              </div>
              {editing === p.id && <OfficeAttendanceForm shift={shift} participant={p} existing={att} onDone={async () => { setEditing(null); await load(); onChanged?.(); }} onCancel={() => setEditing(null)} />}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function OfficeAttendanceForm({ shift, participant, existing, onDone, onCancel }) {
  const [status, setStatus] = useState(existing?.status || 'ATTENDED');
  const [start, setStart] = useState(existing?.actual_start?.slice(11, 16) || participant.start || '');
  const [end, setEnd] = useState(existing?.actual_end?.slice(11, 16) || participant.end || '');
  const [reason, setReason] = useState(existing?.reason_code || '');
  const [notes, setNotes] = useState(existing?.notes || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const out = officeAttendanceBody({ participantId: participant.id, status, date: shift.date, start, end, reason, notes });
    if (out.error) { setError(out.error); return; }
    setError(''); setBusy(true);
    try { await api.post(`/shifts/${shift.id}/attendance`, out.body); toast.success('Participant attendance recorded'); onDone(); }
    catch (e) { setError(e?.response?.data?.detail?.message || e?.response?.data?.error?.message || 'Could not record attendance'); notify.error(e, 'ATTENDANCE_SAVE_FAILED'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-1 space-y-2 rounded-md border bg-muted/30 p-2" data-testid={`office-attendance-form-${participant.id}`}>
      <div className="flex flex-wrap gap-1">
        {STATUS.map(([v, l]) => (
          <button key={v} type="button" onClick={() => setStatus(v)} className={`rounded-md border px-2 py-1 text-xs font-medium ${status === v ? 'bg-primary text-primary-foreground border-primary' : 'bg-background'}`} data-testid={`office-attendance-status-${v}`}>{l}</button>
        ))}
      </div>
      {status !== 'NO_SHOW' && (
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Actual start</Label><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="office-attendance-actual-start" /></div>
          <div><Label className="text-xs">Actual end</Label><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="office-attendance-actual-end" /></div>
        </div>
      )}
      {status !== 'ATTENDED' && (
        <>
          <select className="w-full rounded-md border bg-background px-2 py-1.5 text-xs" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="office-attendance-reason">
            <option value="">Reason…</option>
            {OFFICE_ATTENDANCE_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Textarea rows={2} placeholder="What happened? (required)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="office-attendance-notes" />
        </>
      )}
      {error && <p className="text-xs text-red-700" data-testid="office-attendance-error">{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" className="h-7" onClick={submit} disabled={busy} data-testid="office-attendance-submit">{busy ? 'Saving…' : 'Save attendance'}</Button>
        <Button size="sm" variant="ghost" className="h-7" onClick={onCancel} data-testid="office-attendance-cancel">Cancel</Button>
      </div>
    </div>
  );
}

export default ParticipantAttendancePanel;
